import React from 'react'
import PageHeader from '@/components/PageHeader'

import { Card, Table, Button, Tag, Popconfirm, Divider, Row, Col, message, Switch } from 'antd'
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import ServiceAlertsModal from '@/views/commandCenter/share/ServiceAlertsModal'
import { ALERT_LEVEL_CONFIG } from '@/views/commandCenter/config'
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils'

// 预警类型选项（与 ServiceAlertsForm 保持一致）
const typeOption = [
  { id: 1, value: '护理预警' },
  { id: 3, value: '到家预警' },
  { id: 4, value: '产康预警' },
  { id: 5, value: '客房预警' },
  { id: 6, value: '餐饮预警' },
  { id: 7, value: '监控预警' },
]

// 阈值类型映射
const thresholdTypeMap = {
  1: '时间型',
  2: '百分比型',
  3: '数量型',
}

// 时间单位映射
const timeUnitMap = {
  minute: '分钟',
  hour: '小时',
  day: '天',
}

// 比较方式映射
const comparisonMap = {
  '>=': '大于等于',
  '>': '大于',
  '<=': '小于等于',
  '<': '小于',
  '=': '等于',
}

// 通知方式映射
const noticeWayMap = {
  1: '钉钉',
  2: '短信',
  3: '站内消息',
}

// 通知对象类型映射
const noticeObjectTypeMap = {
  role: '角色',
  user: '人员',
  department: '部门',
}

// 规则类型映射：问题预警 / 完成率预警
const ruleTypeMap = {
  alert: '问题预警',
  completion_rate: '完成率预警',
}

class ServiceAlertsRulesList extends React.Component {
  constructor(props) {
    super(props)
    this.modalTriggerRef = React.createRef()
    this.state = {
      // TODO: 从接口获取数据，这里先用模拟数据
      rulesList: [
        {
          id: 1,
          rule_type: 'alert',
          alert_type: 1,
          alert_level: 3,
          threshold_type: 1,
          threshold_value: 30,
          threshold_unit: 'minute',
          comparison_operator: '>=',
          notice_way: [1, 2],
          notice_object_type: 'role',
          notice_objects: ['role_1', 'role_2'],
          notice_content: '【护理预警】客户{customer_name}的{alert_type_name}预警已超过{threshold_value}{unit}未处理',
          enabled: true,
          created_at: '2024-01-01 10:00:00',
          updated_at: '2024-01-15 14:30:00',
        },
        {
          id: 2,
          rule_type: 'completion_rate', // 完成率预警示例
          alert_type: 1,
          alert_level: 2,
          threshold_type: 2,
          threshold_value: 80,
          threshold_unit: null,
          comparison_operator: '<=',
          notice_way: [1],
          notice_object_type: 'user',
          notice_objects: ['user_1'],
          notice_content: '【护理预警】护理服务完成率仅为{current_rate}%，低于阈值{threshold_value}%',
          enabled: true,
          created_at: '2024-01-05 09:00:00',
          updated_at: '2024-01-10 16:20:00',
        },
        {
          id: 3,
          rule_type: 'alert',
          alert_type: 3,
          alert_level: 4,
          threshold_type: 1,
          threshold_value: 60,
          threshold_unit: 'minute',
          comparison_operator: '>=',
          notice_way: [1, 2, 3],
          notice_object_type: 'department',
          notice_objects: ['dept_1'],
          notice_content: '【到家预警】客户{customer_name}的预警已超时{exceed_time}分钟',
          enabled: true,
          created_at: '2024-01-08 11:00:00',
          updated_at: '2024-01-12 10:15:00',
        },
      ],
      editingRule: null, // 当前编辑的规则
      modalVisible: false,
    }
  }

  /**
   * 按预警类型分组规则
   */
  groupRulesByType = () => {
    const { rulesList } = this.state
    const grouped = {}

    typeOption.forEach((type) => {
      grouped[type.id] = {
        typeId: type.id,
        typeName: type.value,
        rules: rulesList.filter((rule) => rule.alert_type === type.id),
      }
    })

    return grouped
  }

  /**
   * 处理编辑
   */
  handleEdit = (record) => {
    this.setState({
      editingRule: record,
      modalVisible: true,
    }, () => {
      // 延迟触发 Modal 显示，确保 state 已更新
      setTimeout(() => {
        if (this.modalTriggerRef.current) {
          this.modalTriggerRef.current.click()
        }
      }, 0)
    })
  }

  /**
   * 处理删除
   */
  handleDelete = (id) => {
    // TODO: 调用删除接口
    this.setState((prevState) => ({
      rulesList: prevState.rulesList.filter((rule) => rule.id !== id),
    }))
    message.success('删除成功')
  }

  /**
   * 处理启用/停用状态切换
   */
  handleToggleEnabled = (record, enabled) => {
    // TODO: 调用接口更新状态
    this.setState((prevState) => ({
      rulesList: prevState.rulesList.map((rule) =>
        rule.id === record.id
          ? { ...rule, enabled, updated_at: new Date().toLocaleString() }
          : rule
      ),
    }))
    message.success(enabled ? '已启用' : '已停用')
  }

  /**
   * 处理新增
   */
  handleAdd = (alertType) => {
    this.setState({
      editingRule: null,
      modalVisible: true,
      defaultAlertType: alertType, // 预设预警类型
    }, () => {
      // 延迟触发 Modal 显示，确保 state 已更新
      setTimeout(() => {
        if (this.modalTriggerRef.current) {
          this.modalTriggerRef.current.click()
        }
      }, 0)
    })
  }

  /**
   * 处理保存（新增或编辑）
   */
  handleSave = (formData) => {
    const { editingRule } = this.state

    if (editingRule) {
      // 编辑
      this.setState((prevState) => ({
        rulesList: prevState.rulesList.map((rule) =>
          rule.id === editingRule.id ? { ...rule, ...formData, updated_at: new Date().toLocaleString() } : rule
        ),
        modalVisible: false,
        editingRule: null,
      }))
      message.success('更新成功')
    } else {
      // 新增
      const newRule = {
        id: Date.now(), // 临时ID，实际应该由后端生成
        ...formData,
        enabled: true,
        created_at: new Date().toLocaleString(),
        updated_at: new Date().toLocaleString(),
      }
      this.setState((prevState) => ({
        rulesList: [...prevState.rulesList, newRule],
        modalVisible: false,
        defaultAlertType: undefined,
      }))
      message.success('新增成功')
    }
  }

  /**
   * 关闭弹窗
   */
  handleModalClose = () => {
    this.setState({
      modalVisible: false,
      editingRule: null,
      defaultAlertType: undefined,
    })
  }

  /**
   * 格式化阈值显示
   */
  formatThreshold = (record) => {
    const { threshold_type, threshold_value, threshold_unit, comparison_operator } = record
    const typeText = thresholdTypeMap[threshold_type] || '-'
    const unitText = threshold_unit ? timeUnitMap[threshold_unit] : ''
    const operatorText = comparisonMap[comparison_operator] || comparison_operator

    if (threshold_type === 1) {
      return `${operatorText} ${threshold_value}${unitText}`
    } else if (threshold_type === 2) {
      return `${operatorText} ${threshold_value}%`
    } else {
      return `${operatorText} ${threshold_value}`
    }
  }

  /**
   * 格式化通知方式显示
   */
  formatNoticeWay = (noticeWay) => {
    if (!noticeWay || !Array.isArray(noticeWay)) return '-'
    return noticeWay.map((way) => noticeWayMap[way]).join('、')
  }

  /**
   * 获取表格列定义
   */
  getColumns = () => {
    return [
      {
        title: '规则类型',
        dataIndex: 'rule_type',
        key: 'rule_type',
        width: 110,
        render: (ruleType) => {
          const text = ruleTypeMap[ruleType] || '-'
          const color = ruleType === 'completion_rate' ? 'cyan' : 'blue'
          return <Tag color={color}>{text}</Tag>
        },
      },
      {
        title: '预警等级',
        dataIndex: 'alert_level',
        key: 'alert_level',
        width: 100,
        render: (level) => {
          const config = ALERT_LEVEL_CONFIG[level] || {}
          return <Tag color={config.color}>{config.text || level}</Tag>
        },
      },
      {
        title: '阈值条件',
        key: 'threshold',
        width: 180,
        render: (_, record) => {
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#666' }}>类型:</span> {thresholdTypeMap[record.threshold_type] || '-'}
              </div>
              <div>
                <span style={{ color: '#666' }}>条件:</span> {this.formatThreshold(record)}
              </div>
            </div>
          )
        },
      },
      {
        title: '通知方式',
        dataIndex: 'notice_way',
        key: 'notice_way',
        width: 120,
        render: (noticeWay) => this.formatNoticeWay(noticeWay),
      },
      {
        title: '通知对象',
        key: 'notice_objects',
        width: 150,
        render: (_, record) => {
          const typeText = noticeObjectTypeMap[record.notice_object_type] || '-'
          const count = record.notice_objects?.length || 0
          return (
            <div>
              <div style={{ marginBottom: 4 }}>
                <span style={{ color: '#666' }}>类型:</span> {typeText}
              </div>
              <div>
                <span style={{ color: '#666' }}>数量:</span> {count}个
              </div>
            </div>
          )
        },
      },
      {
        title: '通知内容',
        dataIndex: 'notice_content',
        key: 'notice_content',
        ellipsis: true,
        render: (text) => (
          <span title={text} style={{ maxWidth: 200, display: 'inline-block' }}>
            {renderTextStr(text)}
          </span>
        ),
      },
      {
        title: '状态',
        dataIndex: 'enabled',
        key: 'enabled',
        width: 100,
        render: (enabled, record) => (
          <Switch
            checked={enabled}
            checkedChildren="启用"
            unCheckedChildren="停用"
            onChange={(checked) => this.handleToggleEnabled(record, checked)}
          />
        ),
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 160,
        render: (text) => formatDateTimeStr(text),
      },
      {
        title: '操作',
        key: 'action',
        width: 140,
        fixed: 'right',
        render: (_, record) => (
          <React.Fragment>
            <span>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => this.handleEdit(record)}
              >
                编辑
              </Button>
            </span>
            <span>
              <Divider type="vertical" />
              <Popconfirm
                title="确定要删除这条规则吗？"
                onConfirm={() => this.handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </span>
          </React.Fragment>
        ),
      },
    ]
  }

  render() {
    const { modalVisible, editingRule, defaultAlertType } = this.state
    const groupedRules = this.groupRulesByType()

    return (
      <div>
        <PageHeader
          title="预警规则列表"
        />
        <div className='i2-container'>
          <Row gutter={[16, 16]}>
            {typeOption.map((type) => {
              const group = groupedRules[type.id]
              const rules = group?.rules || []

              return (
                <Col key={type.id} span={24}>
                  <Card
                    className="i2-card-page-min"
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{type.value}</span>
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => this.handleAdd(type.id)}
                        >
                          新增规则
                        </Button>
                      </div>
                    }
                    extra={
                      <Tag color="blue">{rules.length} 条规则</Tag>
                    }
                  >
                    <Table
                      className="report-table"
                      columns={this.getColumns()}
                      dataSource={rules}
                      rowKey="id"
                      pagination={rules.length > 10 ? { pageSize: 10 } : false}
                      size="small"
                      scroll={{ x: 1200 }}
                      locale={{ emptyText: '暂无规则，点击"新增规则"添加' }}
                    />
                  </Card>
                </Col>
              )
            })}
          </Row>

          {/* 编辑/新增弹窗 */}
          <ServiceAlertsModal
            baseProps={{
              editingRule,
              defaultAlertType,
              onSave: this.handleSave,
              onCancel: this.handleModalClose,
            }}
            title={editingRule ? '编辑预警规则' : '新增预警规则'}
          >
            <Button
              ref={this.modalTriggerRef}
              type="primary"
              style={{ display: 'none' }}
            >
              触发弹窗
            </Button>
          </ServiceAlertsModal>
        </div>
      </div>
    )
  }
}

export default ServiceAlertsRulesList
